import { BitmapLayer, IconLayer } from "@deck.gl/layers";
import type { CompositeLayer, Layer, PickingInfo } from "deck.gl";
import DeckGL, { DeckGLProps } from "@deck.gl/react";
import { GetCursor, MapType, TooltipType, interactionStateType, mapUrlByType } from "types/map.type";
import { Map, ViewState } from "react-map-gl/mapbox";
import { ModeViewType, mapboxStyle } from "constants/map.const";

import { CurbZone } from "types/curb-zone.type";
import { MAPBOX_ACCESS_TOKEN } from "constants/mapbox.const";
import { TileLayer } from "@deck.gl/geo-layers";
import { ViewportPlaceType } from "types";
import { createMarkerLayer } from "helper/map-utils";
import { useAddControlMapbox } from "hooks/use-add-control-mapbox";
import { useMemo } from "react";

// Define interface for tile bbox properties
interface BoundingBoxCoordinates {
  west: number;
  south: number;
  east: number;
  north: number;
}

export type MapBoxProps = Pick<DeckGLProps, "ref" | "onViewStateChange"> & {
  markerPosition: ViewportPlaceType | null;
  mapType: MapType;
  viewport: ViewState;
  layers: (
    | Layer<object>
    | CompositeLayer<object>
    | IconLayer<object>
    | null
    | (Layer<object> | CompositeLayer<object>)[]
  )[];
  handleSelectLayer: (event: PickingInfo) => void;
  handleDragEnd: (event: PickingInfo) => void;
  handleZooming: (input: interactionStateType) => void;
  hoverInfo?: TooltipType<CurbZone>;
  modeView: ModeViewType;
  isSpaceBarPressed?: boolean;
  getCursor: () => GetCursor;
  onHover?: (info: PickingInfo) => void;
  children?: React.ReactNode;
};

export const MapStyle = ({
  mapType,
  markerPosition,
  viewport,
  layers,
  handleSelectLayer,
  handleDragEnd,
  handleZooming,
  onViewStateChange,
  modeView,
  ref,
  getCursor,
  onHover,
  children,
}: MapBoxProps) => {
  const mapStyle = mapboxStyle[modeView];
  const { mapRefCallback } = useAddControlMapbox();
  const markerLayer = useMemo(() => {
    if (markerPosition) {
      return createMarkerLayer({ coordinates: [markerPosition.longitude, markerPosition.latitude] });
    }
    return null;
  }, [markerPosition]);

  const mapLayers = useMemo(() => {
    if (mapType === MapType.MAPBOX) {
      if (markerLayer) {
        return [...layers, markerLayer];
      }
      return layers;
    }
    const tileLayer = new TileLayer({
      id: "sat-other-map",
      data: mapUrlByType[mapType],

      minZoom: 0,
      maxZoom: 19,
      tileSize: 256,

      renderSubLayers: ({ tile, data, ...props }) => {
        const bbox = tile.bbox as BoundingBoxCoordinates;
        const { west, south, east, north } = bbox;

        return new BitmapLayer(props, {
          image: data,
          bounds: [west, south, east, north],
        });
      },
    });
    if (markerLayer) {
      return [tileLayer, ...layers, markerLayer];
    }
    return [tileLayer, ...layers];
  }, [layers, mapType, markerLayer]);

  return (
    <DeckGL
      initialViewState={{ ...viewport }}
      controller={{
        touchZoom: true,
        touchRotate: true,
      }}
      getCursor={getCursor()}
      layers={mapLayers}
      pickingRadius={12}
      onClick={handleSelectLayer}
      onDragEnd={handleDragEnd}
      onInteractionStateChange={handleZooming}
      onHover={onHover}
      ref={ref}
      onViewStateChange={onViewStateChange}
    >
      <Map
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={mapStyle}
        style={{ width: "100%", height: "100%" }}
        ref={mapRefCallback}
      />
      {children}
    </DeckGL>
  );
};
